// =============================================================================
// SIREPPAR - Panel del Apoderado (Candidaturas Gaiman 2026)
// =============================================================================
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { convocatoriaService, candidatoService } from '../../services/api';

interface EstadoApoderado {
  convocatoria: {
    id: string;
    denominacion: string;
    fechaActo: string;
    cargo: string;
    plazos: Record<string, string>;
  };
  partido: { denominacion: string; sigla: string | null; tipo: string } | null;
  habilitada: boolean;
  motivoDeshabilitacion: string | null;
  candidato: { id: string; estado: string; expedienteId: string | null } | null;
  observacionesActivas: { count: number; fechaLimite: string | null; primeraObservacionId: string } | null;
}

function formatFechaCorta(fecha: string): string {
  const [anio, mes, dia] = fecha.slice(0, 10).split('-');
  return `${dia}/${mes}/${anio}`;
}

function diasRestantes(fecha?: string): number | null {
  if (!fecha) return null;
  const destino = new Date(fecha).getTime();
  const hoy = new Date().setHours(0, 0, 0, 0);
  return Math.ceil((destino - hoy) / (1000 * 60 * 60 * 24));
}

const PLAZO_LABELS: Record<string, string> = {
  cierre_candidaturas: 'Cierre de presentación de candidaturas',
  plazo_subsanacion: 'Plazo de subsanación',
  resolucion_tep: 'Oficialización TEP',
  audiencia_bus: 'Audiencia BUS',
  inicio_campana: 'Inicio de campaña',
  fin_campana: 'Fin de campaña',
  acto_electoral: 'Acto electoral',
};

export default function ApoderadoPanelPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: estado, isLoading, error } = useQuery<EstadoApoderado>({
    queryKey: ['apoderado-mi-estado'],
    queryFn: async () => (await convocatoriaService.getMiEstado()).data.data,
  });

  const expedienteId = estado?.candidato?.estado !== 'BORRADOR' ? estado?.candidato?.expedienteId : null;

  useEffect(() => {
    if (expedienteId) navigate(`/expedientes/${expedienteId}`, { replace: true });
  }, [expedienteId, navigate]);

  const iniciarCarga = async () => {
    if (!estado) return;
    const res = await candidatoService.crearBorrador(estado.convocatoria.id, user!.partidoId!);
    navigate(`/apoderado/candidatura/${res.data.data.id}`);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-red-600">Error al cargar la convocatoria</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

   if (!estado) return null;
   if (expedienteId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const dias = diasRestantes(estado.convocatoria.plazos?.cierre_candidaturas);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-teal-50 to-cyan-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">🏛️</div>
            <div>
              <h1 className="font-bold text-gray-900">{estado.partido?.denominacion || 'Panel del apoderado'}</h1>
              <p className="text-sm text-gray-500">{user?.nombre} {user?.apellido} {estado.partido?.tipo && `· ${estado.partido.tipo}`}</p>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard')} className="text-gray-500 hover:text-gray-700 text-sm">← Volver al inicio</button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {dias !== null && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
            Convocatoria activa con plazo próximo a vencer. Tenés <strong>{dias >= 0 ? `${dias} días` : 'el plazo vencido'}</strong> hasta el cierre de presentación de candidaturas.
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{estado.convocatoria.denominacion}</h2>
              <p className="text-sm text-gray-500">Acto electoral: {formatFechaCorta(estado.convocatoria.fechaActo)}</p>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">En curso</span>
          </div>

          <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
            <strong>Categoría habilitada para tu agrupación:</strong> {estado.convocatoria.cargo}.
          </div>

          {!estado.habilitada ? (
            <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-sm text-red-700">
              Tu agrupación no está habilitada para presentar candidatos en esta convocatoria
              {estado.motivoDeshabilitacion && <> ({estado.motivoDeshabilitacion})</>}.
              Comunicate con la Secretaría Electoral si creés que esto es un error.
            </div>
          ) : estado.candidato ? (
            <div className="border border-gray-100 rounded-lg p-4 bg-gray-50 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-medium text-gray-900">{estado.convocatoria.cargo}</div>
                <div className="text-sm text-gray-500 mt-0.5">Estado: {estado.candidato.estado}</div>
              </div>
              <button
                onClick={() => navigate(`/apoderado/candidatura/${estado.candidato!.id}`)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
              >
                Continuar edición
              </button>
            </div>
          ) : (
            <div className="border border-gray-100 rounded-lg p-4 bg-gray-50 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-medium text-gray-900">{estado.convocatoria.cargo}</div>
                <div className="text-sm text-gray-500 mt-0.5">Lista sin iniciar</div>
              </div>
              <button
                onClick={iniciarCarga}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
              >
                + Iniciar carga de candidato
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Cronograma clave</h3>
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(PLAZO_LABELS).map(([key, label]) => (
                estado.convocatoria.plazos?.[key] && (
                  <tr key={key} className="border-t border-gray-100">
                    <td className="py-2 text-gray-600">{label}</td>
                    <td className="py-2 text-right font-medium">{formatFechaCorta(estado.convocatoria.plazos[key])}</td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
